import { useApolloClient } from "@apollo/client";
import ContactsIcon from "@mui/icons-material/AssignmentInd";
import { Box, Link, Stack, Typography } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { Link as RouterLink, useParams } from "react-router-dom";
import { RestrictedByOrganisationPermissionWithDebug } from "../../auth/RestrictedByOrganisationPermission.js";
import { Page, PageTopbarItem } from "../../commons/layout/Page.js";
import { PageContainer } from "../../commons/layout/PageContainer.js";
import { OrganisationAvatar } from "../../features/organisations/OrganisationAvatar.js";
import { useSupplierProfileQuery } from "./SupplierProfile.generated.js";

interface Props {
  submenuItems: PageTopbarItem[];
}

export const SupplierProfile = ({
  submenuItems,
  children,
}: React.PropsWithChildren<Props>) => {
  const { supplierId } = useParams();
  if (!supplierId) throw new Error("Supplier id is missing");

  const { t } = useTranslate(["CrmOrganisations"]);

  const client = useApolloClient();
  const query = useSupplierProfileQuery({
    client,
    variables: {
      supplierId,
    },
  });

  const result = query.data?.pimSupplier;
  const supplier = result?.supplier;
  const crmCompany = supplier?.linkedCrmCompany;

  return (
    <Page title={supplier?.title} submenuItems={submenuItems}>
      <PageContainer>
        {supplier && (
          <Stack direction={"column"} spacing={2}>
            <Stack
              direction="row"
              spacing={2}
              sx={{
                justifyContent: "flex-start",
                alignItems: "stretch",
              }}
            >
              <OrganisationAvatar organisationAvatar={supplier} size="xl" />
              <Stack
                direction="column"
                spacing={0.5}
                sx={{
                  flex: 1,
                  alignSelf: "center",
                }}
              >
                <Typography variant="h2">{supplier.title}</Typography>
                {crmCompany?.id && (
                  <RestrictedByOrganisationPermissionWithDebug permission="READ_CRM">
                    <Link
                      component={RouterLink}
                      to={`/crm/organisations/${crmCompany.id}`}
                      variant="body2"
                      underline="hover"
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.5,
                        alignSelf: "flex-start",
                      }}
                    >
                      <ContactsIcon fontSize="small" />
                      {t("Open in CRM", { ns: "CrmOrganisations" })}
                    </Link>
                  </RestrictedByOrganisationPermissionWithDebug>
                )}
              </Stack>
            </Stack>
            <Box>{children}</Box>
          </Stack>
        )}
      </PageContainer>
    </Page>
  );
};
